/**
 * Hours types — the payroll classification of an hour worked, seeded from the
 * WinTeam export. Inactive types are carried for history and never offered for
 * selection anywhere else in the app.
 *
 * `premium_multiplier` is PAY COST only. It never touches a bill rate.
 */
import React, { useEffect, useState } from 'react';
import { hoursTypesAPI } from '../lib/api';
import { useCap } from '../components/auth/RequireCap';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import { Clock, Plus, Pencil, Archive, ArchiveRestore } from 'lucide-react';

const empty = {
  name: '', legacy_winteam_id: '', gl_wage_account: '', is_overtime: false,
  is_exempt_from_ot: false, premium_multiplier: '', exclude_premium_dollars: false,
  default_billable: true, compare_to_budget: true, is_active: true,
};

function HoursTypeDialog({ open, onOpenChange, row, onSaved }) {
  const [form, setForm] = useState({ ...empty });
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    setForm(row ? { ...empty, ...row, premium_multiplier: row.premium_multiplier ?? '' } : { ...empty });
  }, [row, open]);
  const change = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        legacy_winteam_id: form.legacy_winteam_id?.trim() || null,
        premium_multiplier: form.premium_multiplier === '' ? null : Number(form.premium_multiplier),
      };
      if (row?.id) await hoursTypesAPI.update(row.id, payload);
      else await hoursTypesAPI.create(payload);
      toast.success('Saved');
      onSaved(); onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const flag = (key, label, hint) => (
    <div className="flex items-start justify-between gap-3 py-2 border-b border-line-subtle last:border-0">
      <div>
        <Label className="text-sm">{label}</Label>
        {hint && <p className="text-xs text-fg-muted mt-0.5 max-w-sm">{hint}</p>}
      </div>
      <Switch checked={!!form[key]} onCheckedChange={(v) => change(key, v)}
        data-testid={`ht-${key}`} />
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="hours-type-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Clock className="w-5 h-5 text-slate-500" />
            {row?.id ? `Hours type — ${row.name}` : 'New hours type'}
          </DialogTitle>
          <DialogDescription>
            The payroll classification of an hour, with its GL wage account.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="md:col-span-2"><Label>Name</Label>
            <Input value={form.name} onChange={(e) => change('name', e.target.value)} data-testid="ht-name" /></div>
          <div><Label>GL wage account</Label>
            <Input value={form.gl_wage_account} onChange={(e) => change('gl_wage_account', e.target.value)} data-testid="ht-gl" /></div>
          <div><Label>WinTeam id</Label>
            <Input value={form.legacy_winteam_id || ''} onChange={(e) => change('legacy_winteam_id', e.target.value)}
              placeholder="Blank if unknown" data-testid="ht-legacy" /></div>
          <div><Label>Premium multiplier</Label>
            <Input type="number" step="0.01" value={form.premium_multiplier}
              onChange={(e) => change('premium_multiplier', e.target.value)} data-testid="ht-premium" />
            <p className="text-xs text-fg-muted mt-1">Pay cost only — never used to work out a bill rate.</p>
          </div>
        </div>
        <div className="mt-2">
          {flag('is_overtime', 'Overtime', 'Hours on this type count as overtime.')}
          {flag('is_exempt_from_ot', 'Exempt from overtime')}
          {flag('exclude_premium_dollars', 'Exclude premium dollars')}
          {flag('default_billable', 'Billable by default',
            'Turn this off and any shift using this type is forced non-billable.')}
          {flag('compare_to_budget', 'Compare to budget')}
          {flag('is_active', 'Active', 'Inactive types stay for history but are never offered for selection.')}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="ht-save">
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function HoursTypes() {
  const canEdit = useCap('admin', 'edit');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await hoursTypesAPI.list(true);
      setRows(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load hours types');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = async (row) => {
    try { await hoursTypesAPI.setActive(row.id, !row.is_active); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not update'); }
  };

  return (
    <div className="space-y-6" data-testid="hours-types-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight">Hours Types</h1>
          <p className="text-sm text-fg-muted mt-1 max-w-2xl">
            Seeded from the WinTeam Hours Types export. Two rows carry no WinTeam id yet —
            that field is deliberately blank rather than guessed at.
          </p>
        </div>
        {canEdit && (
          <Button onClick={() => { setEditing(null); setOpen(true); }} data-testid="ht-create-btn">
            <Plus className="w-4 h-4 mr-2" />New hours type
          </Button>
        )}
      </div>

      <Card><CardContent className="p-0">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Name</TableHead>
            <TableHead>WinTeam</TableHead>
            <TableHead>GL</TableHead>
            <TableHead>Behaviour</TableHead>
            <TableHead className="w-24 text-right">Actions</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={5} className="p-6 text-sm text-fg-muted">Loading…</TableCell></TableRow>
            ) : rows.map(r => (
              <TableRow key={r.id} data-testid={`ht-row-${r.id}`}>
                <TableCell className="font-medium">
                  <div className="flex items-center gap-2">
                    {r.name}
                    {!r.is_active && <Badge variant="secondary">Inactive · history only</Badge>}
                  </div>
                </TableCell>
                <TableCell className="font-mono text-xs">{r.legacy_winteam_id || '—'}</TableCell>
                <TableCell className="font-mono text-xs">{r.gl_wage_account || '—'}</TableCell>
                <TableCell className="text-xs">
                  <div className="flex flex-wrap gap-1">
                    {r.is_overtime && <Badge variant="outline">Overtime</Badge>}
                    {r.is_exempt_from_ot && <Badge variant="outline">OT exempt</Badge>}
                    {r.exclude_premium_dollars && <Badge variant="outline">No premium $</Badge>}
                    {!r.default_billable && <Badge variant="outline">Non-billable</Badge>}
                    {r.premium_multiplier ? <Badge variant="outline">×{r.premium_multiplier} pay</Badge> : null}
                  </div>
                </TableCell>
                <TableCell className="text-right">
                  {canEdit && (
                    <>
                      <Button size="icon" variant="ghost" onClick={() => { setEditing(r); setOpen(true); }}
                        data-testid={`ht-edit-${r.id}`}><Pencil className="w-4 h-4" /></Button>
                      <Button size="icon" variant="ghost" onClick={() => toggle(r)}
                        data-testid={`ht-toggle-${r.id}`}>
                        {r.is_active ? <Archive className="w-4 h-4" /> : <ArchiveRestore className="w-4 h-4" />}
                      </Button>
                    </>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent></Card>

      <HoursTypeDialog open={open} onOpenChange={setOpen} row={editing} onSaved={load} />
    </div>
  );
}
